/**
 * @FIXME consider adding this to component-library
 */
import type {UseUpliftAdsReturn} from '@/modules/common/hooks/useUpliftAds/useUpliftAds';

import {AditionSlot, UpliftAds} from '@travel-frontend/ui-components';
import {memo, useRef, useState} from 'react';
import useResizeObserver from 'use-resize-observer';

import {BannerStack} from '@/modules/common/components/Adition/components/BannerStack';
import {SkyscraperBannerContainer} from '@/modules/common/components/Adition/components/SkyscraperBannerContainer';
import {StickyAditionSlot} from '@/modules/common/components/Adition/components/StickyAditionSlot';
import {useUpliftAds} from '@/modules/common/hooks';

type SlotConfig = {
  additionId: string;
  upliftAdId: number;
  sticky: boolean;
  width: number;
  height: number;
};

type UpliftAds = Record<number, UseUpliftAdsReturn>;

const FIRST_UPLIFT_AD_ID = import.meta.env
  .VITE_UPLIFT_ADS_FIRST_SKYSCRAPER_ZONE_ID;
const SECOND_UPLIFT_AD_ID = import.meta.env
  .VITE_UPLIFT_ADS_SECOND_SKYSCRAPER_ZONE_ID;

const MIN_WIDTHS_WIDE = 300;
const MIN_WIDTHS_NARROW = 160;

// note: static to keep things simple
const bannerSlots: Record<string, Array<SlotConfig>> = {
  wide: [
    {
      additionId: 'DSK_Urlaubsreisen_Buchungsstrecke_MR_300x250',
      upliftAdId: FIRST_UPLIFT_AD_ID,
      sticky: false,
      width: MIN_WIDTHS_WIDE,
      height: 250,
    },
    {
      additionId: 'DSK_Urlaubsreisen_Buchungsstrecke_WS_300x600',
      upliftAdId: SECOND_UPLIFT_AD_ID,
      sticky: true,
      width: MIN_WIDTHS_WIDE,
      height: 600,
    },
  ],
  narrow: [
    {
      additionId: 'DSK_Urlaubsreisen_Buchungsstrecke_WS_160x600',
      upliftAdId: SECOND_UPLIFT_AD_ID,
      sticky: true,
      width: MIN_WIDTHS_NARROW,
      height: 600,
    },
  ],
};

type Props = {
  stickyOffset?: number;
};

export const SkyscraperBanners = memo(function SkyscraperBanners({
  stickyOffset = 0,
}: Props) {
  const [slots, setSlots] = useState<Array<SlotConfig> | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const padding = 16;

  useResizeObserver({
    ref: document.body,
    onResize: ({width}) => {
      // https://github.com/ZeeCoder/use-resize-observer/issues/38
      requestAnimationFrame(() => {
        const containerRect = containerRef.current?.getBoundingClientRect();
        if (width && containerRect) {
          const availableWidth = width - containerRect.left;
          if (availableWidth >= MIN_WIDTHS_WIDE) {
            setSlots(bannerSlots.wide);
          } else if (availableWidth >= MIN_WIDTHS_NARROW) {
            setSlots(bannerSlots.narrow);
          } else {
            setSlots(null);
          }
        }
      });
    },
  });

  const upliftAds: UpliftAds = {
    [FIRST_UPLIFT_AD_ID]: useUpliftAds(FIRST_UPLIFT_AD_ID),
    [SECOND_UPLIFT_AD_ID]: useUpliftAds(SECOND_UPLIFT_AD_ID),
  };

  return (
    <SkyscraperBannerContainer ref={containerRef}>
      {slots?.length && (
        <BannerStack padding={padding}>
          {slots.map((slot) => (
            <StickyAditionSlot
              key={slot.additionId}
              enabled={slot.sticky}
              stickyOffset={stickyOffset + padding}
            >
              {upliftAds[slot.upliftAdId].shouldShowBanner ? (
                <UpliftAds
                  category="skyscraper"
                  advertisements={upliftAds[slot.upliftAdId].advertisement}
                  width={`${slot.width}px`}
                  height={`${slot.height}px`}
                />
              ) : (
                <AditionSlot id={slot.additionId} />
              )}
            </StickyAditionSlot>
          ))}
        </BannerStack>
      )}
    </SkyscraperBannerContainer>
  );
});
